import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { authApi } from '../services/api';
import { getApiErrorMessage } from '../lib/apiError';

const AuthContext = createContext(null);

// This browser's "trusted for 30 days" token, one per account.
const trustKey = (email) => `mfaTrusted:${(email || '').toLowerCase()}`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('accessToken');
      const savedUser = localStorage.getItem('user');
      
      if (token && savedUser) {
        try {
          const response = await authApi.me();
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (err) {
          // Token might be expired, try to refresh
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            try {
              const refreshResponse = await authApi.refresh(refreshToken);
              localStorage.setItem('accessToken', refreshResponse.data.accessToken);
              localStorage.setItem('refreshToken', refreshResponse.data.refreshToken);
              
              const meResponse = await authApi.me();
              setUser(meResponse.data);
              localStorage.setItem('user', JSON.stringify(meResponse.data));
            } catch (refreshErr) {
              // Refresh failed, clear everything
              localStorage.removeItem('accessToken');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('user');
            }
          }
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  // Keep localStorage in sync when user changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }
  }, [user]);

  // Attach the current user to Sentry events (no-op if Sentry isn't configured)
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email, username: user.name });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  // Cross-tab auth sync: when another tab logs out or refreshes tokens,
  // mirror the change here so we don't keep stale state or kick the user
  // to /login while a sibling tab successfully re-authenticated.
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'user') {
        if (e.newValue) {
          try {
            setUser(JSON.parse(e.newValue));
          } catch (_) {
            // ignore malformed payload
          }
        } else {
          // Another tab logged out (logout() and the 401 handler both clear the
          // `user` key). We intentionally do NOT log out on an `accessToken`
          // removal alone: during a token-refresh race a sibling tab can briefly
          // clear tokens, and reacting to that would cascade-log-out every tab
          // ("we all get logged out"). The `user` key is the authoritative
          // cross-tab logout signal.
          setUser(null);
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Keep permissions fresh without a reload: the backend now resolves
  // authorization from the database on every request, so re-read /auth/me when
  // the tab regains focus (throttled) to keep the UI's gating in step with any
  // permission change an admin just made.
  useEffect(() => {
    let lastCheck = Date.now();
    const refreshMe = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!localStorage.getItem('accessToken')) return;
      if (Date.now() - lastCheck < 60000) return;
      lastCheck = Date.now();
      try {
        const res = await authApi.me();
        setUser(res.data);
      } catch (_) {
        // ignore — the axios 401 handler owns session recovery
      }
    };
    window.addEventListener('focus', refreshMe);
    document.addEventListener('visibilitychange', refreshMe);
    return () => {
      window.removeEventListener('focus', refreshMe);
      document.removeEventListener('visibilitychange', refreshMe);
    };
  }, []);

  const storeSession = useCallback((data, email) => {
    const { accessToken, refreshToken, user: userData, trustedDeviceToken } = data;
    if (trustedDeviceToken && email) localStorage.setItem(trustKey(email), trustedDeviceToken);
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    // Notify contexts that auth state changed (storage event only fires cross-tab)
    window.dispatchEvent(new Event('auth-changed'));
    setUser(userData);
    return userData;
  }, []);

  // Resolves to the user, or to { mfa } when a 2FA step must come first.
  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await authApi.login(email, password, localStorage.getItem(trustKey(email)) || undefined);
      if (response.data.mfaRequired) return { mfa: response.data };
      return storeSession(response.data, email);
    } catch (err) {
      const message = getApiErrorMessage(err, 'Login failed');
      setError(message);
      throw new Error(message);
    }
  }, [storeSession]);

  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } catch (err) {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setUser(null);
    }
  }, []);

  const hasPermission = useCallback((resource, action) => {
    if (!user?.permissions) return false;
    // Admin role always has full access
    if (user.role === 'admin') return true;

    const adminPerms = user.permissions.admin || {};

    // Mirror the backend permission cascade (utils/auth.check_permission):
    //  - admin.systemSettings grants the whole "settings" resource
    //  - admin.manageIntegrations / admin.systemSettings grant every
    //    per-integration management flag.
    if (resource === 'settings' && adminPerms.systemSettings) return true;
    const PER_INTEGRATION_FLAGS = ['manageEmail', 'manageNinjaOne', 'manageXero', 'manageM365'];
    if (resource === 'admin' && PER_INTEGRATION_FLAGS.includes(action)) {
      if (adminPerms.manageIntegrations || adminPerms.systemSettings) return true;
    }
    // Granular per-Settings-page flags: when the flag is ABSENT, inherit from
    // systemSettings (mirrors backend SETTINGS_PAGE_FLAGS cascade). Explicit wins.
    const SETTINGS_PAGE_FLAGS = [
      'manageWorkflows', 'manageNoteTemplates', 'manageNotifications',
      'manageCompletionRules', 'manageAiSettings',
      'manageValues', 'manageAssetConfig', 'manageClientFields',
      'manageStorageLocations', 'manageTaxRates',
      'manageSla', 'manageHealthChecks', 'viewStorageUsage',
      'viewIntegrationHealth', 'viewSyncLogs', 'manageLogSettings',
      'manageCompany', 'manageStyling', 'manageSystem',
    ];
    if (resource === 'admin' && SETTINGS_PAGE_FLAGS.includes(action)) {
      if (!(action in adminPerms) && adminPerms.systemSettings) return true;
    }

    return user.permissions[resource]?.[action] || false;
  }, [user]);

  const isAdmin = useCallback(() => {
    return user?.role === 'admin';
  }, [user]);

  const value = {
    user,
    setUser,
    loading,
    error,
    login,
    completeLogin: storeSession,
    logout,
    hasPermission,
    isAdmin,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
